import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPayrollSummaryReport } from "../../../../store/thunks/reportsThunk";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const PayrollSummaryReport = () => {
  const dispatch = useDispatch();
  const { data: payrollData, loading, error } = useSelector(
    (state) => state.reports.payrollSummary || { data: null, loading: false, error: null }
  );

  // Filter state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split("T")[0];
  });

  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split("T")[0];
  });

  const [selectedClinics, setSelectedClinics] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedClinicians, setSelectedClinicians] = useState([]);
  const [activeTab, setActiveTab] = useState("summary"); // summary, byClinician, byClinic, byStatus

  // Get clinics and clinicians from Redux
  const clinics = useSelector((state) => state.clinics.clinics || []);
  const users = useSelector((state) => state.users.users || []);
  const clinicians = users.filter((user) => user.userRole === "clinician");

  // Payroll status options
  const statusOptions = ["pending", "ready", "approved", "paid", "carryover"];

  // Load initial data on mount
  useEffect(() => {
    handleSubmit();
  }, []);

  const handleSubmit = async () => {
    console.log(`[Payroll Summary Report] Fetching report for ${startDate} to ${endDate}`);

    try {
      await dispatch(
        fetchPayrollSummaryReport({
          startDate,
          endDate,
          clinics: selectedClinics.length > 0 ? selectedClinics : undefined,
          statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
          clinicianIds: selectedClinicians.length > 0 ? selectedClinicians : undefined,
        })
      ).unwrap();

      toast.success("Payroll summary report loaded successfully");
      console.log(`[Payroll Summary Report] Report loaded successfully`);
    } catch (err) {
      console.error(`[Payroll Summary Report] Error loading report:`, err);
      toast.error(err?.message || "Failed to load payroll summary report");
    }
  };

  const handleDownloadExcel = () => {
    if (!payrollData) {
      toast.warning("No data available to export");
      return;
    }

    console.log(`[Payroll Summary Report] Exporting to Excel - Active tab: ${activeTab}`);

    let worksheet;
    let filename;

    if (activeTab === "summary") {
      // Export summary data
      const summaryData = [
        ["Payroll Summary Report"],
        [`Period: ${startDate} to ${endDate}`],
        [],
        ["Metric", "Value"],
        ["Total Payroll Amount", formatCurrency(payrollData.summary?.totalPayroll || 0)],
        ["Average Payroll", formatCurrency(payrollData.summary?.avgPayroll || 0)],
        ["Total Entries", payrollData.summary?.totalEntries || 0],
        ["Unique Clinicians", payrollData.summary?.uniqueCliniciansCount || 0],
      ];

      worksheet = XLSX.utils.aoa_to_sheet(summaryData);
      filename = `Payroll_Summary_${startDate}_to_${endDate}.xlsx`;
    } else if (activeTab === "byClinician") {
      // Export by clinician
      const clinicianData = [
        ["Payroll by Clinician"],
        [`Period: ${startDate} to ${endDate}`],
        [],
        ["Clinician", "Total Amount", "Average Amount", "Entry Count", "Statuses"],
        ...(payrollData.byClinician || []).map((item) => [
          item.clinicianName,
          item.totalAmount,
          item.avgAmount,
          item.count,
          item.statuses?.join(", ") || "",
        ]),
      ];

      worksheet = XLSX.utils.aoa_to_sheet(clinicianData);
      filename = `Payroll_By_Clinician_${startDate}_to_${endDate}.xlsx`;
    } else if (activeTab === "byClinic") {
      // Export by clinic
      const clinicData = [
        ["Payroll by Clinic"],
        [`Period: ${startDate} to ${endDate}`],
        [],
        ["Clinic", "Total Amount", "Entry Count", "Unique Clinicians"],
        ...(payrollData.byClinic || []).map((item) => [
          item.clinicName,
          item.totalAmount,
          item.count,
          item.uniqueCliniciansCount,
        ]),
      ];

      worksheet = XLSX.utils.aoa_to_sheet(clinicData);
      filename = `Payroll_By_Clinic_${startDate}_to_${endDate}.xlsx`;
    } else if (activeTab === "byStatus") {
      // Export by status
      const statusData = [
        ["Payroll by Status"],
        [`Period: ${startDate} to ${endDate}`],
        [],
        ["Status", "Total Amount", "Entry Count"],
        ...(payrollData.byStatus || []).map((item) => [
          item.status,
          item.totalAmount,
          item.count,
        ]),
      ];

      worksheet = XLSX.utils.aoa_to_sheet(statusData);
      filename = `Payroll_By_Status_${startDate}_to_${endDate}.xlsx`;
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    XLSX.writeFile(workbook, filename);

    toast.success("Excel file downloaded successfully");
    console.log(`[Payroll Summary Report] Excel exported: ${filename}`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const handleClinicToggle = (clinicId) => {
    setSelectedClinics((prev) =>
      prev.includes(clinicId)
        ? prev.filter((id) => id !== clinicId)
        : [...prev, clinicId]
    );
  };

  const handleStatusToggle = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleClinicianToggle = (clinicianId) => {
    setSelectedClinicians((prev) =>
      prev.includes(clinicianId)
        ? prev.filter((id) => id !== clinicianId)
        : [...prev, clinicianId]
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Payroll Summary Report
          </h1>
          <p className="text-gray-600">
            Comprehensive overview of payroll entries, clinician compensation, and status breakdowns
          </p>
        </div>

        {/* Filter Panel */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Filters</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Clinics Multi-Select */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clinics (Optional)
            </label>
            <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto bg-white">
              {clinics.length === 0 ? (
                <p className="text-gray-500 text-sm">No clinics available</p>
              ) : (
                clinics.map((clinic) => (
                  <label
                    key={clinic._id}
                    className="flex items-center space-x-2 py-1 cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedClinics.includes(clinic._id)}
                      onChange={() => handleClinicToggle(clinic._id)}
                      className="rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      {clinic.clinicName}
                    </span>
                  </label>
                ))
              )}
            </div>
            {selectedClinics.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {selectedClinics.length} clinic(s) selected
              </p>
            )}
          </div>

          {/* Statuses Multi-Select */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payroll Status (Optional)
            </label>
            <div className="flex flex-wrap gap-2">
              {statusOptions.map((status) => (
                <button
                  key={status}
                  onClick={() => handleStatusToggle(status)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    selectedStatuses.includes(status)
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
            {selectedStatuses.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {selectedStatuses.length} status(es) selected
              </p>
            )}
          </div>

          {/* Clinicians Multi-Select */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clinicians (Optional)
            </label>
            <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto bg-white">
              {clinicians.length === 0 ? (
                <p className="text-gray-500 text-sm">No clinicians available</p>
              ) : (
                clinicians.map((clinician) => (
                  <label
                    key={clinician._id}
                    className="flex items-center space-x-2 py-1 cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedClinicians.includes(clinician._id)}
                      onChange={() => handleClinicianToggle(clinician._id)}
                      className="rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      {clinician.firstName} {clinician.lastName}
                    </span>
                  </label>
                ))
              )}
            </div>
            {selectedClinicians.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {selectedClinicians.length} clinician(s) selected
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? "Loading..." : "Generate Report"}
            </button>
            <button
              onClick={handleDownloadExcel}
              disabled={!payrollData || loading}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              Download Excel
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">
              Error loading report: {error.message || "Unknown error"}
            </p>
          </div>
        )}

        {/* Results */}
        {payrollData && !loading && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setActiveTab("summary")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === "summary"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Summary
                </button>
                <button
                  onClick={() => setActiveTab("byClinician")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === "byClinician"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  By Clinician
                </button>
                <button
                  onClick={() => setActiveTab("byClinic")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === "byClinic"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  By Clinic
                </button>
                <button
                  onClick={() => setActiveTab("byStatus")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === "byStatus"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  By Status
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            {activeTab === "summary" && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Overall Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Total Payroll</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(payrollData.summary?.totalPayroll || 0)}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">
                      Average Payroll
                    </p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(payrollData.summary?.avgPayroll || 0)}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Total Entries</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {payrollData.summary?.totalEntries || 0}
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">
                      Unique Clinicians
                    </p>
                    <p className="text-2xl font-bold text-orange-600">
                      {payrollData.summary?.uniqueCliniciansCount || 0}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "byClinician" && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Payroll by Clinician
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Clinician
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Average Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Entry Count
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statuses
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(payrollData.byClinician || []).map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.clinicianName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.totalAmount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.avgAmount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.count}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {item.statuses?.join(", ") || "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(payrollData.byClinician || []).length === 0 && (
                    <p className="text-center text-gray-500 py-8">
                      No clinician data available
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeTab === "byClinic" && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Payroll by Clinic
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Clinic
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Entry Count
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Unique Clinicians
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(payrollData.byClinic || []).map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.clinicName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.totalAmount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.uniqueCliniciansCount}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(payrollData.byClinic || []).length === 0 && (
                    <p className="text-center text-gray-500 py-8">
                      No clinic data available
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeTab === "byStatus" && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Payroll by Status
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Entry Count
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(payrollData.byStatus || []).map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.status?.charAt(0).toUpperCase() +
                              item.status?.slice(1)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.totalAmount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(payrollData.byStatus || []).length === 0 && (
                    <p className="text-center text-gray-500 py-8">
                      No status data available
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* No Data Message */}
        {!payrollData && !loading && !error && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500 text-lg">
              Click "Generate Report" to load payroll summary data
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollSummaryReport;
